import { openImageInNewTab } from '../../../utils/imageHelpers';
// src/components/student/activities/ActivityDetailModal.jsx

import React from "react";
import {
  X,
  BookOpen,
  Calendar,
  Tag,
  FileText,
  Image as ImageIcon,
  Palette,
  FolderOpen,
  Download,
  Sheet,
  Presentation,
  FileType,
  Award,
  CheckCircle2,
  XCircle,
  Star,
  MessageSquareQuote,
  PenTool,
} from "lucide-react";
import { formatDateForDisplay } from "../../../utils/dateHelpers";
import { notify } from '../../../utils/notify';
import {
  downloadDocument,
  getDocumentIcon,
  getDocumentColor,
  formatFileSize,
} from "../../../utils/documentCompression";

/**
 * Professional activity detail modal with full information
 * UPDATED: Added documents section with download functionality
 */
const ActivityDetailModal = ({ isOpen, onClose, activity }) => {
  if (!isOpen || !activity) return null;

  /**
   * Get icon component based on extension
   */
  const getIconComponent = (extension) => {
    const iconName = getDocumentIcon(extension);

    switch (iconName) {
      case "FileText":
        return FileText;
      case "Sheet":
        return Sheet;
      case "Presentation":
        return Presentation;
      case "FileType":
        return FileType;
      default:
        return FileText;
    }
  };

  /**
   * Handle document download
   */
  const handleDownload = (doc) => {
    try {
      downloadDocument(doc.data, doc.name);
    } catch (error) {
      console.error("Download failed:", error);
      notify.error("Failed to download document. Please try again.");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="
          bg-white rounded-xl shadow-2xl
          max-w-4xl w-full my-8
          animate-in zoom-in-95 fade-in duration-200
        "
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-start justify-between rounded-t-xl z-10">
          <div className="flex-1 pr-4">
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              {activity.title}
            </h2>

            {/* Meta Info */}
            <div className="flex flex-wrap items-center gap-3 text-sm">
              {activity.date && (
                <span className="inline-flex items-center gap-1.5 text-gray-600">
                  <Calendar className="w-4 h-4" />
                  {formatDateForDisplay(activity.date)}
                </span>
              )}

              {activity.type && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full font-medium">
                  <Tag className="w-3.5 h-3.5" />
                  {activity.type}
                </span>
              )}

              {(activity.procedure_category || activity.procedureCategory) && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-indigo-50 text-indigo-700 rounded-full font-semibold border border-indigo-200">
                  Category: {activity.procedure_category || activity.procedureCategory}
                </span>
              )}

              {(activity.procedure_sub_type || activity.procedureSubtype) && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-purple-50 text-purple-700 rounded-full font-semibold border border-purple-200">
                  Procedure: {activity.procedure_sub_type || activity.procedureSubtype}
                </span>
              )}

              {activity.attemptType && (
                <span
                  className={`
                    inline-flex items-center px-2.5 py-1 rounded-full font-medium
                    ${
                      activity.attemptType === "First(F)"
                        ? "bg-green-50 text-green-700"
                        : activity.attemptType === "Repeat(R)"
                          ? "bg-yellow-50 text-yellow-700"
                          : "bg-orange-50 text-orange-700"
                    }
                  `}
                >
                  {activity.attemptType}
                </span>
              )}
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors flex-shrink-0"
            aria-label="Close modal"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-6 space-y-6 max-h-[calc(90vh-200px)] overflow-y-auto">
          {/* Description/Content */}
          {activity.content && (
            <section>
              <div className="flex items-center gap-2 mb-3">
                <FileText className="w-5 h-5 text-gray-600" />
                <h3 className="text-lg font-semibold text-gray-900">
                  Description
                </h3>
              </div>
              <div
                className="prose prose-sm max-w-none text-gray-700 bg-gray-50 rounded-lg p-4 border border-gray-200"
                dangerouslySetInnerHTML={{ __html: activity.content }}
              />
            </section>
          )}

          {/* Drawing */}
          {activity.drawing && (
            <section>
              <div className="flex items-center gap-2 mb-3">
                <Palette className="w-5 h-5 text-gray-600" />
                <h3 className="text-lg font-semibold text-gray-900">Drawing</h3>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <img
                  src={activity.drawing}
                  alt="Activity drawing"
                  className="max-w-full h-auto rounded-lg border border-gray-300 shadow-sm"
                />
              </div>
            </section>
          )}

          {/* Images */}
          {activity.images && activity.images.length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-3">
                <ImageIcon className="w-5 h-5 text-gray-600" />
                <h3 className="text-lg font-semibold text-gray-900">
                  Images ({activity.images.length})
                </h3>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {activity.images.map((image, index) => (
                  <div
                    key={index}
                    className="relative aspect-square rounded-lg overflow-hidden border border-gray-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer group"
                  >
                    <img
                      src={image}
                      alt={`Activity image ${index + 1}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                      onClick={() => openImageInNewTab(image, `Activity Image ${index + 1}`)}
                    />
                  </div>
                ))}
              </div>
            </section>
          )}

          
          {/* Competencies */}
          {activity.competencies && activity.competencies.length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-3">
                <BookOpen className="w-5 h-5 text-indigo-600" />
                <h3 className="text-lg font-semibold text-gray-900">
                  Competencies ({activity.competencies.length})
                </h3>
              </div>
              <div className="space-y-2">
                {activity.competencies.map((comp, index) => (
                  <div key={index} className="bg-slate-50 border border-slate-200 rounded-xl p-3.5 flex items-start gap-3 shadow-xs">
                    <span className="px-2.5 py-1 rounded-lg bg-white text-gray-900 border border-gray-300 font-mono text-xs font-bold shrink-0 shadow-xs">
                      {comp.code}
                    </span>
                    <p className="text-sm font-medium text-slate-800 leading-relaxed">
                      {comp.description}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* ✅ ADDED: Documents Section */}
          {activity.documents && activity.documents.length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-3">
                <FolderOpen className="w-5 h-5 text-gray-600" />
                <h3 className="text-lg font-semibold text-gray-900">
                  Documents ({activity.documents.length})
                </h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {activity.documents.map((doc, index) => {
                  const extension =
                    doc.extension ||
                    doc.name?.split(".").pop()?.toLowerCase() ||
                    "file";
                  const IconComponent = getIconComponent(extension);
                  const colorClass = getDocumentColor(extension);

                  return (
                    <div
                      key={index}
                      className="
                        bg-white border border-gray-200 rounded-lg p-4
                        hover:border-gray-300 hover:shadow-sm
                        transition-all duration-200
                      "
                    >
                      <div className="flex items-start gap-3">
                        {/* Icon */}
                        <div className={`flex-shrink-0 ${colorClass}`}>
                          <IconComponent className="w-8 h-8" />
                        </div>

                        {/* Details */}
                        <div className="flex-1 min-w-0">
                          {/* File name */}
                          <p className="text-sm font-medium text-gray-900 truncate mb-1">
                            {doc.name}
                          </p>

                          {/* File info */}
                          <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                            <span className="uppercase font-medium">
                              {extension}
                            </span>
                            <span>•</span>
                            <span>
                              {formatFileSize(
                                doc.size || doc.originalSize || 0,
                              )}
                            </span>
                          </div>

                          {/* Download button */}
                          <button
                            onClick={() => handleDownload(doc)}
                            className="
                              inline-flex items-center gap-1.5 px-3 py-1.5
                              text-xs font-medium text-blue-700 bg-blue-50
                              hover:bg-blue-100 rounded-md
                              transition-colors duration-200
                            "
                          >
                            <Download className="w-3.5 h-3.5" />
                            Download
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* Assessment & Review Sections (Hierarchical: Faculty first, HOD second) */}
          {(() => {
            const facultyComment = activity.comments?.find((c) => c.from_source !== "HOD") || activity.comments?.[0];
            const facultyReaction = activity.reactions?.find((r) => r.from_source !== "HOD") || activity.reactions?.[0];

            const hodComment = activity.comments?.find((c) => c.from_source === "HOD");
            const hodReaction = activity.reactions?.find((r) => r.from_source === "HOD");

            const hasFacultyAssessment = !!facultyComment || !!facultyReaction;
            const hasHodAssessment = !!hodComment || !!hodReaction;

            if (!hasFacultyAssessment && !hasHodAssessment) return null;

            const renderCard = (title, iconColor, commentObj, reactionObj, isHod = false) => {
              if (!commentObj && !reactionObj) return null;

              const isApp = reactionObj?.type === "✅" || reactionObj?.decision === "Approved" || commentObj?.status === "✅" || commentObj?.assessment?.facultyDecision === "Approved";
              const isRej = reactionObj?.type === "❌" || reactionObj?.decision === "Rejected" || commentObj?.status === "❌" || commentObj?.assessment?.facultyDecision === "Rejected";

              const evalName = commentObj?.from || reactionObj?.from || (isHod ? "Head of Department" : "Faculty Member");
              const evalSubrole = commentObj?.subrole || reactionObj?.subrole || (isHod ? "Head of Department (HOD)" : "Faculty Member");
              const evalTimestamp = commentObj?.timestamp || reactionObj?.timestamp;
              const ratingVal = commentObj?.assessment?.rating || reactionObj?.rating;
              const scoreVal = (commentObj?.assessment?.numericScore !== undefined && commentObj?.assessment?.numericScore !== null)
                ? commentObj.assessment.numericScore
                : reactionObj?.score;
              const feedbackText = commentObj?.text;
              const signatureUrl = commentObj?.signature || reactionObj?.signature;

              return (
                <section className="bg-slate-50/90 border border-slate-200/90 rounded-2xl p-5 shadow-xs space-y-4">
                  <div className="flex items-center justify-between border-b border-slate-200/80 pb-3">
                    <div className="flex items-center gap-2">
                      <div className={`p-2 rounded-xl border ${isHod ? "bg-purple-50 border-purple-100 text-purple-600" : "bg-indigo-50 border-indigo-100 text-indigo-600"}`}>
                        <Award className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="text-base font-bold text-slate-900">{title}</h3>
                        <p className="text-xs text-slate-600 font-semibold mt-0.5">
                          By <span className="text-indigo-700 font-bold">{evalSubrole}: {evalName}</span>
                          {evalTimestamp && ` · ${formatDateForDisplay(evalTimestamp)}`}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Status Badges & Key Metrics */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {/* Decision */}
                    <div className={`p-3 rounded-xl border flex items-center gap-3 ${
                      isApp ? "bg-emerald-50 border-emerald-200 text-emerald-900" : isRej ? "bg-rose-50 border-rose-200 text-rose-900" : "bg-amber-50 border-amber-200 text-amber-900"
                    }`}>
                      {isApp ? <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" /> : isRej ? <XCircle className="w-5 h-5 text-rose-600 shrink-0" /> : <Award className="w-5 h-5 text-amber-600 shrink-0" />}
                      <div>
                        <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500 block">Decision</span>
                        <span className="font-extrabold text-xs">{isApp ? "Approved ✅" : isRej ? "Rejected ❌" : "Pending"}</span>
                      </div>
                    </div>

                    {/* Rating */}
                    <div className="p-3 rounded-xl border border-slate-200 bg-white flex items-center gap-3">
                      <div className="p-1.5 rounded-lg bg-amber-50 text-amber-600 border border-amber-100 shrink-0">
                        <Star className="w-4 h-4 fill-amber-400 text-amber-500" />
                      </div>
                      <div>
                        <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500 block">Rating</span>
                        <span className="font-bold text-xs text-slate-800">{ratingVal || "Satisfactory"}</span>
                      </div>
                    </div>

                    {/* Numeric Score */}
                    <div className="p-3 rounded-xl border border-slate-200 bg-white flex items-center gap-3">
                      <div className="p-1.5 rounded-lg bg-indigo-50 text-indigo-600 border border-indigo-100 shrink-0">
                        <Award className="w-4 h-4 text-indigo-600" />
                      </div>
                      <div>
                        <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500 block">Score</span>
                        <span className="font-bold text-xs text-indigo-700">{scoreVal !== undefined && scoreVal !== null && scoreVal !== "" ? `${scoreVal} / 10` : "Not Scored"}</span>
                      </div>
                    </div>
                  </div>

                  {/* Feedback Text */}
                  {feedbackText && (
                    <div className="bg-white border border-slate-200/90 rounded-xl p-3.5 shadow-2xs">
                      <div className="flex items-center gap-2 mb-1.5">
                        <MessageSquareQuote className="w-4 h-4 text-indigo-600" />
                        <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider">Review Comments & Feedback</h4>
                      </div>
                      <p className="text-xs text-slate-800 leading-relaxed whitespace-pre-wrap pl-5 border-l-2 border-indigo-400">{feedbackText}</p>
                    </div>
                  )}

                  {/* E-Signature */}
                  {signatureUrl && (
                    <div className="bg-white border border-slate-200 rounded-xl p-2.5 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <PenTool className="w-4 h-4 text-emerald-600" />
                        <span className="text-xs font-semibold text-slate-700">Official E-Signature</span>
                      </div>
                      <img src={signatureUrl} alt="E-Signature" className="h-9 object-contain max-w-[140px] border border-slate-100 rounded bg-slate-50 p-1" />
                    </div>
                  )}
                </section>
              );
            };

            return (
              <div className="space-y-4">
                {renderCard("Faculty Review & Assessment", "indigo", facultyComment, facultyReaction, false)}
                {renderCard("HOD Verification & Review", "purple", hodComment, hodReaction, true)}
              </div>
            );
          })()}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 rounded-b-xl">
          <button
            onClick={onClose}
            className="
              w-full px-4 py-2.5
              bg-white text-gray-700 text-sm font-medium
              border border-gray-300 rounded-lg hover:bg-gray-50
              transition-colors duration-200
              focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2
            "
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ActivityDetailModal;

//30
